import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { CalendarDays, ArrowLeft, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Employee-facing time-off request form.
 * Reached from /paypunch after entering employee code → "Request Time Off" btn.
 * Requires ?code=EMPCODE query param (falls back to prompting).
 */
export default function RequestTimeOffPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get("code");
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    request_type: "paid",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/timeclock/verify-employee`, { code });
        setEmployee(res.data.employee);
        const h = await axios.get(`${API}/hr/requests`, { params: { status: "all", actor_code: code } });
        setHistory(h.data.requests || []);
      } catch (e) {
        toast.error("Employee not found — go back and enter your code");
      }
    })();
  }, [code]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Pick start and end dates");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/hr/requests`, {
        employee_code: code,
        ...form,
      });
      toast.success("Request submitted — waiting for approval");
      const h = await axios.get(`${API}/hr/requests`, { params: { status: "all", actor_code: code } });
      setHistory(h.data.requests || []);
      setForm({ request_type: "paid", start_date: "", end_date: "", reason: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (!code) {
    return (
      <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-6">
        <Card className="p-6 max-w-sm">
          <p className="text-sm text-gray-600 mb-4">
            No employee code provided. Go back to the timecard and enter your code first.
          </p>
          <Button onClick={() => navigate("/paypunch")} className="w-full">
            Back to timecard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f5] px-4 py-6">
      <div className="max-w-lg mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/paypunch")}
          className="text-gray-600 mb-3 pl-1"
          data-testid="btn-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <Card className="p-5 mb-4" data-testid="req-form-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1A5E1F]/10 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-[#1A5E1F]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Request Time Off</h1>
              <p className="text-xs text-gray-500">
                {employee?.name || "…"} · #{code}
              </p>
            </div>
          </div>

          <label className="block text-xs font-semibold text-gray-700 mb-1">Type</label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { v: "paid", l: "Paid" },
              { v: "unpaid", l: "Unpaid" },
              { v: "short_term", l: "Short-term leave" },
              { v: "long_term", l: "Long-term leave" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setForm({ ...form, request_type: opt.v })}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  form.request_type === opt.v
                    ? "bg-[#1A5E1F] text-white border-[#1A5E1F]"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"
                }`}
                data-testid={`type-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Start date</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="input-start"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">End date</label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="input-end"
              />
            </div>
          </div>

          <label className="block text-xs font-semibold text-gray-700 mb-1">Reason (optional)</label>
          <Textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Wedding, medical, family, etc."
            className="mb-4 min-h-[72px]"
            data-testid="input-reason"
          />

          <Button
            onClick={submit}
            disabled={busy}
            className="w-full bg-[#1A5E1F] hover:bg-[#164e19] text-white h-11"
            data-testid="btn-submit"
          >
            <Send className="w-4 h-4 mr-2" />
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </Card>

        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 px-1">
          Your requests
        </div>
        {history.length === 0 && (
          <Card className="p-6 text-center text-sm text-gray-500">
            No requests yet
          </Card>
        )}
        <div className="space-y-2">
          {history.map((r) => (
            <Card key={r.id} className="p-3" data-testid={`hist-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">
                  {r.start_date} → {r.end_date} ({r.days}d)
                </span>
                <span>
                  <Badge className={`border-0 text-[10px] mr-1 ${TYPES[r.request_type] || TYPES.paid}`}>
                    {(r.request_type || "").replace("_", " ").toUpperCase()}
                  </Badge>
                  <Badge className={`border-0 text-[10px] ${STATUS[r.status] || STATUS.pending}`}>
                    {(r.status || "").toUpperCase()}
                  </Badge>
                </span>
              </div>
              {r.reason && <div className="text-xs text-gray-500 italic">"{r.reason}"</div>}
              {r.status === "denied" && r.denied_reason && (
                <div className="text-xs text-red-600 mt-1">Reason: {r.denied_reason}</div>
              )}
              {r.status !== "pending" && r.approved_by_name && (
                <div className="text-[11px] text-gray-500 mt-1">
                  by {r.approved_by_name}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

const TYPES = {
  paid: "bg-green-100 text-green-700",
  unpaid: "bg-gray-200 text-gray-700",
  short_term: "bg-blue-100 text-blue-700",
  long_term: "bg-purple-100 text-purple-700",
};
const STATUS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  denied: "bg-red-100 text-red-700",
};
